import './Pacing.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MdHelp } from 'react-icons/md';
import {
  MetricsTablePreset,
  PACING as PACING_MEDIATYPE
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import { RatioGoalConflict, pacingGoalTypeUsesKpi } from '@blisspointmedia/bpm-types/dist/Pacing';
import {
  computeCustomRelativeRange,
  computeDateFromPreset
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { FilterPreset } from '@blisspointmedia/bpm-types/dist/pgTables/FilterPreset';
import {
  DeletePresetParams as DeletePagePresetParams,
  MetricsPagePreset,
  SavePresetParams as SavePagePresetParams,
  SavePresetResponse as SavePagePresetResponse
} from '@blisspointmedia/bpm-types/dist/MetricsPage';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { Dropdown } from '@Components/Dropdown/Dropdown';
import { IconButton } from '@Components/IconButton/IconButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { Page } from '@Components/Page/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import {
  Appearance,
  Background,
  Design,
  Intent,
  Placement,
  Size
} from '@Components/designConstants';
import { FetchFilterBar, useFilterPaneState } from '@Components/FetchFilterBar/FetchFilterBar';
import { PresetDropdown } from '@Components/PresetDropdown/PresetDropdown';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { SourcePicker } from '@Pages/CrossChannel/Components/SourcePicker';
import { EditPagePresetModal } from '@Pages/CrossChannel/EditPagePresetModal';
import { buildCustomSegmentDimensionMap } from '@Pages/CrossChannel/cross-channel-utils';
import { ColumnMetaDataMap } from '@Components/MetricsTable/metrics-table-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { useSetError } from '@src/redux/modals';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { DateRange } from '@src/utils/date-utils';
import { PacingDashboard } from './PacingDashboard';
import { PacingPresetTableDashboard } from './PacingPresetTableDashboard';
import {
  CrossChannelPacingTableData,
  DataMode,
  Simplicity,
  TableView,
  TableViewType
} from './pacing-constants';
import { pacingDimensionColumnMetaDataMap } from './pacingMetricsTableConfig';
import { resolvePacingMode } from './pacingFetchKeys';
import {
  barsTreeHasGoal,
  goalNameToKey,
  isPacingRatioGoalType,
  ratioConflictSignature,
  timePeriodProgress
} from './pacingUtils';
import { RatioGoalConflictModal } from './components/RatioGoalConflictModal';
import {
  parsePacingDataVarName,
  PacingMetric,
  pacingShowKpiForMetric,
  pacingShowSourceForMetric
} from './pacingPresetColumnPicker';
import {
  deletePagePreset as deletePagePresetRequest,
  deleteTablePreset as deleteTablePresetRequest,
  getTablePreset as getTablePresetRequest,
  savePagePreset as savePagePresetRequest,
  saveTablePreset as saveTablePresetRequest,
  SaveTablePresetBody,
  usePacingBars,
  usePacingFilterOptions,
  usePacingOptions,
  usePacingTableData,
  usePagePresets,
  useTablePresets
} from './use-pacing';

const TabKey = {
  PACING: 'pacing-bars',
  PERFORMANCE_TABLE: 'performance-table'
} as const;
type TabKey = (typeof TabKey)[keyof typeof TabKey];

const NAVS = [
  { label: 'Pacing', key: TabKey.PACING },
  { label: 'Pacing and Performance Table', key: TabKey.PERFORMANCE_TABLE }
];

const DATE_FORMAT = 'yyyy-MM-dd';

// Label shared by menuTabs + extraTabs; the modal matches the tab by string equality.
const TABLE_VIEW_TAB = 'Table View';

// Narrow a caught/query error to a message string, keeping the non-Error case safe.
const toErrorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));

// Mutually-exclusive filter selection for a table request: a saved filter id, a raw filter state, or
// neither.
const buildFilterInfo = <F,>(
  filterID: number | undefined,
  filterState: F
): { filterID: number } | { filterState: F } | Record<string, never> => {
  if (filterID != null) {
    return { filterID };
  }
  if (filterState) {
    return { filterState };
  }
  return {};
};

interface SourceKpiPickersProps {
  selectedSources: string[];
  setSelectedSources: (sources: string[]) => void;
  sourceOptions: { value: string; label: string }[];
  uniquePlatforms: string[];
  selectedKpi: string;
  setKpi: (kpi: string) => void;
  kpiOptions: { label: string; value: string }[];
}

// Shared Source + KPI pickers for the bars tab and the Pacing and Performance Table tab.
const SourceKpiPickers = ({
  selectedSources,
  setSelectedSources,
  sourceOptions,
  uniquePlatforms,
  selectedKpi,
  setKpi,
  kpiOptions
}: SourceKpiPickersProps) => (
  <>
    <SourcePicker
      size={Size.MD}
      selectedSources={selectedSources}
      sourceOptions={sourceOptions}
      applyOnClick={setSelectedSources}
      platforms={uniquePlatforms}
    />
    <Dropdown
      overlayPlacement={Placement.RIGHT}
      label={kpiOptions.length > 0 ? 'KPI' : 'No KPI options available'}
      value={selectedKpi}
      onChange={val => {
        if (val !== selectedKpi) {
          setKpi(val);
        }
      }}
      options={kpiOptions}
      disabled={kpiOptions.length === 0}
    />
  </>
);

interface PagePresetTableViewTabProps {
  pagePresetChanges: MetricsPagePreset;
  setPagePresetChanges: (changes: MetricsPagePreset) => void;
}

// The page-preset modal's pacing-only "Table View" tab: Simple/Adjusted + Summary/Over Time toggles.
const PagePresetTableViewTab = ({
  pagePresetChanges,
  setPagePresetChanges
}: PagePresetTableViewTabProps) => (
  <div className='pacing-page-preset-table-view'>
    <SegmentedControlButton
      design={Design.NEUTRAL}
      options={[Simplicity.SIMPLE, Simplicity.ADJUSTED]}
      selectedOption={pagePresetChanges.globalOptions?.simplicity ?? Simplicity.SIMPLE}
      onChange={value =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: { ...pagePresetChanges.globalOptions, simplicity: value }
        })
      }
    />
    <SegmentedControlButton
      design={Design.NEUTRAL}
      options={[TableView.SUMMARY, TableView.OVER_TIME]}
      selectedOption={pagePresetChanges.globalOptions?.tableView ?? TableView.SUMMARY}
      onChange={value =>
        setPagePresetChanges({
          ...pagePresetChanges,
          globalOptions: { ...pagePresetChanges.globalOptions, tableView: value }
        })
      }
    />
  </div>
);

export const Pacing = () => {
  const company = useLocationCompany() ?? '';
  const setError = useSetError();
  const companyInfo = useCompanyInfo();

  const prefix = PACING_MEDIATYPE;

  // Table Preset State
  const [tablePresetName, setTablePresetName] = useState<string>();
  // Applied preset id — resolved in preference to name (names can collide, e.g. Temp Preview's throwaway).
  const [tablePresetId, setTablePresetId] = useState<number>();
  const [tablePresetMap, setTablePresetMap] = useState<Record<string, MetricsTablePreset>>({});

  // Filter preset state. FetchFilterBar (bpm-v2-filters service, platform "pacing") owns its own
  // preset CRUD; we just hold the selection + categories and thread filterID/filterState into the fetch.
  const [filterID, setFilterID] = useState<number | undefined>();
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>();
  const [filterSource, setFilterSource] = useState<
    'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState' | undefined
  >();
  const [filterState, setFilterState, resetFilterState] = useFilterPaneState();

  // Page preset state (table tab). Snapshots the settings that drive the Performance Table (dates,
  // source, KPI, simplicity, table view, table preset, filter) and restores them on load. Bars-only
  // state (goal, dimensions, dataMode) is intentionally excluded. New + Edit both go through the
  // shared EditPagePresetModal; pacing uses a single exact date range.
  const [pagePresetName, setPagePresetName] = useState<string>();
  const [pagePresetChanges, setPagePresetChanges] = useState<MetricsPagePreset>();
  const [showNewPagePresetModal, setShowNewPagePresetModal] = useState(false);
  const [showEditPagePresetModal, setShowEditPagePresetModal] = useState(false);
  const [showRenamePagePresetModal, setShowRenamePagePresetModal] = useState(false);

  const [dataMode, setDataMode] = useState<string>(DataMode.ABSOLUTE);
  const [simplicity, setSimplicity] = useState<string>(Simplicity.SIMPLE);
  // Preset-table view toggle: "Summary" (one row per bucket) vs "Over Time" (Date-grouped, buckets as
  // super-headers). Both row sets ship in one response, so the toggle is client-side (see below).
  const [tableView, setTableView] = useState<TableViewType>(TableView.SUMMARY);
  const presetTableOverTime = tableView === TableView.OVER_TIME;
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [currentGoalValue, setCurrentGoalValue] = useState<string>('Budget');

  const [dateRange, setDateRange] = useState<DateRange>({
    start: Dfns.format(DATE_FORMAT, Dfns.startOfMonth(new Date())),
    end: Dfns.format(DATE_FORMAT, Dfns.endOfMonth(new Date()))
  });
  const [activeDimensions, setActiveDimensions] = useState<string[]>(['Channel']);

  const goal = currentGoalValue;
  const { start, end } = dateRange;

  // Source + KPI selection — page-level pickers shared by the bars and table tabs.
  const [kpi, setKpi] = useState<string>('');
  const [selectedSources, setSelectedSources] = useState<string[]>(['platform']);
  const sourcesParam = useMemo(() => selectedSources.join(','), [selectedSources]);
  const dimensionsString = useMemo(() => activeDimensions.join(','), [activeDimensions]);

  const queryClient = useQueryClient();

  // Options for every dropdown/picker (goal types, segments, source/KPI combos, platforms) plus the
  // filter categories and the table/page preset lists. All four are React Query reads (use-pacing.ts);
  // readers below use each hook's `data` directly.
  const { data: pacingOptions = null, error: pacingOptionsError } = usePacingOptions(
    company,
    start,
    end
  );
  const { data: filterData } = usePacingFilterOptions(company);
  const { data: tablePresets, isFetching: fetchingTablePresets } = useTablePresets(company, prefix);
  const { data: pagePresets, isFetching: fetchingPagePresets } = usePagePresets(company, prefix);

  // Preset writes: plain requests (use-pacing.ts) that invalidate the matching React Query list on
  // completion. The by-name cache (tablePresetMap) is populated lazily by getTablePreset below.
  const invalidateTablePresets = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pacingTablePresets', company, prefix] });
  }, [queryClient, company, prefix]);

  const invalidatePagePresets = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pacingPagePresets', company, prefix] });
  }, [queryClient, company, prefix]);

  const getTablePreset = useCallback(
    async (params: {
      id: number;
      company: string;
      mediatype: string;
    }): Promise<MetricsTablePreset> => {
      try {
        const preset = await getTablePresetRequest(params);
        if (preset && preset.name) {
          setTablePresetMap(map => ({ ...map, [preset.name]: preset }));
        }
        return preset;
      } catch (e) {
        setError({
          message: toErrorMessage(e),
          title: 'Failed to fetch table preset',
          reportError: e
        });
        throw e;
      }
    },
    [setError]
  );

  const saveTablePreset = useCallback(
    async (body: SaveTablePresetBody): Promise<{ id: number; name: string }> => {
      try {
        return await saveTablePresetRequest(body);
      } catch (e) {
        setError({
          message: toErrorMessage(e),
          title: 'Failed to save table preset',
          reportError: e
        });
        throw e;
      } finally {
        // A save can overwrite an existing id/name, leaving the by-name cache stale — clear it and
        // refetch the list.
        setTablePresetMap({});
        invalidateTablePresets();
      }
    },
    [invalidateTablePresets, setError]
  );

  const deleteTablePreset = useCallback(
    async (params: { id: number; mediatype: string }): Promise<void> => {
      try {
        await deleteTablePresetRequest(params);
        Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.PACING, entity: 'table preset' });
      } catch (e) {
        setError({
          message: toErrorMessage(e),
          title: 'Failed to delete table preset',
          reportError: e
        });
      } finally {
        invalidateTablePresets();
      }
    },
    [invalidateTablePresets, setError]
  );

  const savePagePreset = useCallback(
    async (body: SavePagePresetParams): Promise<SavePagePresetResponse | undefined> => {
      try {
        return await savePagePresetRequest(body);
      } catch (e) {
        setError({
          message: `Failed to save page preset. Error: ${toErrorMessage(e)}`,
          reportError: e instanceof Error ? e : undefined
        });
        return undefined;
      } finally {
        invalidatePagePresets();
      }
    },
    [invalidatePagePresets, setError]
  );

  const deletePagePreset = useCallback(
    async (params: DeletePagePresetParams): Promise<void> => {
      try {
        await deletePagePresetRequest(params);
        Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.PACING, entity: 'page preset' });
      } catch (e) {
        setError({
          message: `Failed to delete page preset. Error: ${toErrorMessage(e)}`,
          reportError: e instanceof Error ? e : undefined
        });
      } finally {
        invalidatePagePresets();
      }
    },
    [invalidatePagePresets, setError]
  );

  const [initialPickDone, setInitialPickDone] = useState(false);

  // Surface an options-fetch failure and unblock the initial pick so the page doesn't hang on it.
  useEffect(() => {
    if (pacingOptionsError) {
      setError({
        message: toErrorMessage(pacingOptionsError),
        title: 'Failed to fetch pacing options',
        reportError: pacingOptionsError
      });
      setInitialPickDone(true);
    }
  }, [pacingOptionsError, setError]);

  useEffect(() => {
    if (!pacingOptions || initialPickDone) {
      return;
    }
    if (pacingOptions.goalTypes.length > 0) {
      const defaultGoal = pacingOptions.goalTypes[0];
      setCurrentGoalValue(defaultGoal);

      const availableSegs = pacingOptions.segmentsPerGoal[defaultGoal] || [];
      if (availableSegs.length > 0) {
        setActiveDimensions([availableSegs[0]]);
      }
    }
    setInitialPickDone(true);
  }, [pacingOptions, initialPickDone]);

  // For performance goals (Revenue / KPI Volume), default the source + KPI to a combo that actually
  // has goals — on initial load and whenever the goal type changes. Budget paces on delivery, so it
  // keeps the current picker selection.
  useEffect(() => {
    const combos = pacingOptions?.sourceKpiPerGoal?.[currentGoalValue];
    if (combos && combos.length > 0) {
      const { source, kpi: comboKpi } = combos[0];
      setSelectedSources([source && source !== 'platform' ? source : 'platform']);
      setKpi(comboKpi);
    }
  }, [currentGoalValue, pacingOptions]);

  // A perf goal (Revenue / KPI Volume) is ready to fetch only once its KPI is reconciled to the goal
  // (by the effect above) — otherwise it would fetch with the previous goal's KPI. Budget is
  // delivery-only, so always ready.
  const perfSelectionReady = useMemo(
    () =>
      // Delivery-only goal types (Budget + ratio CPC/CPM/CTR) don't need a KPI; KPI-scoped types
      // wait until the picked KPI reconciles to a source-KPI combo.
      !pacingGoalTypeUsesKpi(goal) ||
      (pacingOptions?.sourceKpiPerGoal?.[goal] ?? []).some(combo => combo.kpi === kpi),
    [goal, kpi, pacingOptions]
  );

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'pacing',
    defaultKey: TabKey.PACING,
    navs: NAVS.map(nav => nav.key)
  });

  // Bars fetch (getCrossChannelPacingBarsV2 runs the whole pipeline). Gated to the bars tab — the
  // KPI/source pickers are shared with the table tab, so a table-tab interaction must not trigger it.
  // Budget paces on delivery (KPI-agnostic), so KPI is blanked from the params (and thus the query
  // key) to avoid a needless refetch when the picker changes. Source stays 'platform' (see below).
  const barsEnabled =
    tab === TabKey.PACING && initialPickDone && perfSelectionReady && Boolean(start && end);
  const barsParams = useMemo(
    () => ({
      company,
      goal,
      activeDimensions: dimensionsString,
      start,
      end,
      kpi: goal === 'Budget' ? '' : kpi,
      // Budget must still send a platform source: the lambda always joins a performance CTE whose
      // table is chosen by sources.includes('platform') (platform_performance vs analytics_performance),
      // and the analytics table isn't guaranteed to exist per company — sending '' would select it and
      // fail the whole bars query. Hardcoding 'platform' also keeps this out of the picker's refetch key.
      sources: goal === 'Budget' ? 'platform' : sourcesParam
    }),
    [company, goal, dimensionsString, start, end, kpi, sourcesParam]
  );
  const {
    data: barsData,
    isFetching: barsFetching,
    isPlaceholderData: barsPlaceholder,
    error: barsError
  } = usePacingBars(barsParams, barsEnabled);
  // A disabled query doesn't fetch, so the previous key's bars would sit on screen uncovered.
  const barsLoading = barsFetching || (!barsEnabled && barsPlaceholder);

  useEffect(() => {
    if (barsError) {
      setError({
        title: "Couldn't load pacing bars",
        message: `Failed to load pacing bars: ${toErrorMessage(barsError)}`,
        reportError: barsError
      });
    }
  }, [barsError, setError]);

  const pacingBarsData = barsData?.pacingBarsData ?? {};
  // Bars exist when the selected goal appears anywhere in the tree — the root (additive) or a child
  // node (a segmented ratio goal, whose root/total is goalless). null until the first response lands.
  const dataExists = barsData
    ? barsTreeHasGoal(barsData.pacingBarsData, goalNameToKey(goal))
    : null;
  // Treat an errored bars fetch as "ready" too, so the page spinner clears and the error modal shows
  // instead of spinning forever behind it.
  const dataReadyBars = (Boolean(barsData) || Boolean(barsError)) && !barsFetching;

  const isFetching = !initialPickDone || (dataExists !== false && !dataReadyBars);

  // Resolve the full MetricsTablePreset for the active tablePresetName from the cache, lazily
  // fetching it when missing, falling back to the first available preset.
  const [resolvedTablePreset, setResolvedTablePreset] = useState<MetricsTablePreset>();
  useEffect(() => {
    if (!tablePresets || fetchingTablePresets) {
      return;
    }
    // Resolve by the applied id first.
    if (tablePresetId != null) {
      // Short-circuit when the by-name cache already holds this id. getTablePreset populates that
      // cache (tablePresetMap, an effect dep), so without this guard the fetch re-triggers the
      // effect and loops.
      const cachedById = tablePresetName ? tablePresetMap[tablePresetName] : undefined;
      if (cachedById && cachedById.id === tablePresetId) {
        setResolvedTablePreset(cachedById);
        return;
      }
      (async () => {
        try {
          const full = await getTablePreset({ id: tablePresetId, company, mediatype: prefix });
          setResolvedTablePreset(full);
        } catch {
          // error surfaced in getTablePreset
        }
      })();
      return;
    }
    // When no table preset is selected yet, fall back to the first non-temporary preset (the seeded
    // Channel / Channel_Platform defaults from the backend, getDefaultPacingTablePreset).
    let targetName = tablePresetName;
    if (targetName == null) {
      const fallbackPreset = tablePresets.find(p => !p.temporary);
      targetName = fallbackPreset?.name;
    }
    if (targetName == null) {
      setResolvedTablePreset(undefined);
      return;
    }
    const cached = tablePresetMap[targetName];
    if (cached) {
      setResolvedTablePreset(cached);
      return;
    }
    const simplePreset = tablePresets.find(p => p.name === targetName);
    if (simplePreset && simplePreset.id != null) {
      (async () => {
        try {
          const full = await getTablePreset({
            id: simplePreset.id,
            company,
            mediatype: prefix
          });
          setResolvedTablePreset(full);
        } catch {
          // error surfaced in getTablePreset
        }
      })();
    }
  }, [
    tablePresets,
    tablePresetName,
    tablePresetId,
    tablePresetMap,
    fetchingTablePresets,
    getTablePreset,
    company,
    prefix
  ]);

  // Ratio-goal collisions to surface in the alert modal (null = hidden); the ref tracks the last
  // shown set so we only re-open when the collisions actually change (resets on page unmount).
  const [conflictModal, setConflictModal] = useState<RatioGoalConflict[] | null>(null);
  const lastShownConflictSig = useRef('');

  // KPI options come from the company's canonical registry; blank entries are dropped.
  const companyKpis = useMemo(
    () => (companyInfo.cross_channel_kpis ?? []).filter(Boolean),
    [companyInfo.cross_channel_kpis]
  );

  const { uniquePlatforms, sourceOptions, kpiOptions } = useMemo(
    () => ({
      uniquePlatforms: pacingOptions?.uniquePlatforms ?? [],
      sourceOptions: pacingOptions?.sourceOptions ?? [],
      kpiOptions: companyKpis.map(value => ({ label: value, value }))
    }),
    [pacingOptions, companyKpis]
  );

  // KPI metadata for the preset editor's per-column KPI dropdown. {id, name} only:
  // convertKPIMapToOptions appends any other non-empty string field to the dropdown label.
  const kpiNameMap = useMemo(() => {
    const map: Record<string, KpiInfo> = {};
    for (const k of companyKpis) {
      map[k] = { id: k, name: k };
    }
    return map;
  }, [companyKpis]);

  useEffect(() => {
    if (kpiOptions.length > 0 && !kpi) {
      setKpi(kpiOptions[0].value);
    }
  }, [kpi, kpiOptions]);

  const selectedKpi = kpi === '' ? (kpiOptions[0]?.value ?? '') : kpi;
  // Comma-joined to match the handler's `activeDimensions.split(",")` grouping (pacingV2/pacingTable).
  const presetTablePresetGroup = useMemo(
    () =>
      resolvedTablePreset
        ? (resolvedTablePreset.dimensionColumns ?? []).map(col => col.dimensionVarName).join(',')
        : '',
    [resolvedTablePreset]
  );
  // The filter-bar selection sent to the table (mutually exclusive: a saved filter preset id, or a
  // serialized filter state), keyed into the cache so a filter change refetches. Covers both the
  // "current*" sources (live bar selection) and the "preset*" ones (a filter restored from a page preset).
  const filterFetchParams = useMemo<{ filterID?: number; filterState?: string }>(() => {
    if (
      (filterSource === 'currentFilterID' || filterSource === 'presetFilterID') &&
      filterID != null
    ) {
      return { filterID };
    }
    if (
      (filterSource === 'currentState' || filterSource === 'presetState') &&
      filterState != null
    ) {
      return { filterState: JSON.stringify(filterState) };
    }
    return {};
  }, [filterSource, filterID, filterState]);
  // The (metric, KPI, source) demand from the preset's shown columns; `kpi`/`source` omitted →
  // "Global", resolved to the page KPI / page source picker in the lambda. Drives which actuals
  // scopes the table fetch requests.
  const goalMetricsParam = useMemo(() => {
    const seen = new Set<string>();
    const demand: { metric: PacingMetric; kpi?: string; source?: string }[] = [];
    for (const col of resolvedTablePreset?.dataColumns ?? []) {
      if (!col.dataVarName) {
        continue;
      }
      const { metric } = parsePacingDataVarName(col.dataVarName);
      // KPI/source apply only to KPI-scoped metrics (Revenue/Volume/ROAS/CPX); delivery-only metrics
      // (spend/CPC/CPM/CTR) resolve against shared delivery.
      const kpiOverride = pacingShowKpiForMetric(metric) ? col.kpi || undefined : undefined;
      const sourceOverride = pacingShowSourceForMetric(metric)
        ? col.source || undefined
        : undefined;
      const key = `${metric}|${kpiOverride ?? ''}|${sourceOverride ?? ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        demand.push({
          metric,
          ...(kpiOverride ? { kpi: kpiOverride } : {}),
          ...(sourceOverride ? { source: sourceOverride } : {})
        });
      }
    }
    return JSON.stringify(demand);
  }, [resolvedTablePreset]);
  // One table fetch (getCrossChannelPacingTableV2) returns both the summary and over-time rows
  // (Simple + Adjusted per row), so the view/mode toggles are client-side. Gated to the table tab and
  // to a resolved preset + loaded options (so the KPI has settled before the first fetch — selectedKpi
  // is the resolved fallback, avoiding a double-fetch as the raw KPI resolves).
  const tableEnabled =
    tab === TabKey.PERFORMANCE_TABLE &&
    Boolean(resolvedTablePreset) &&
    Boolean(pacingOptions) &&
    Boolean(start && end);
  const tableParams = useMemo(
    () => ({
      company,
      start,
      end,
      activeDimensions: presetTablePresetGroup,
      kpi: selectedKpi,
      sources: sourcesParam,
      goalMetrics: goalMetricsParam,
      ...filterFetchParams
    }),
    [
      company,
      start,
      end,
      presetTablePresetGroup,
      selectedKpi,
      sourcesParam,
      goalMetricsParam,
      filterFetchParams
    ]
  );
  const {
    data: tableResult,
    isFetching: tableFetching,
    error: tableError
  } = usePacingTableData(tableParams, tableEnabled);

  const presetSummaryData = useMemo(() => tableResult?.summary ?? [], [tableResult]);
  const presetOverTimeData = useMemo(() => tableResult?.overTime ?? [], [tableResult]);
  const presetTableDataReady = Boolean(tableResult) && !tableFetching;

  // Page-level loading, resolved per tab. `isFetching` is bars-specific (barsEnabled gates the bars
  // query to the bars tab), so on the table tab it never clears — use the table's own readiness there.
  // Drives the telemetry-only `<Page isLoading>` (times the load, fires MxE.PageDataLoad) and the
  // DatePicker's disabled state.
  const pageLoading = tab === TabKey.PERFORMANCE_TABLE ? !presetTableDataReady : isFetching;

  useEffect(() => {
    if (tableError) {
      setError({
        title: "Couldn't load pacing table",
        message: `Failed to load pacing table: ${toErrorMessage(tableError)}`,
        reportError: tableError
      });
    }
  }, [tableError, setError]);

  // Surface ratio-goal collisions once per distinct set — refetches that yield the same collisions
  // (regroup, KPI/source, Simple/Adjusted) don't re-open the modal.
  useEffect(() => {
    const conflicts = tableResult?.conflicts ?? [];
    const conflictSig = ratioConflictSignature(conflicts);
    if (conflictSig !== lastShownConflictSig.current) {
      lastShownConflictSig.current = conflictSig;
      if (conflicts.length > 0) {
        setConflictModal(conflicts);
      }
    }
  }, [tableResult]);

  // Pick the active view (Summary vs Over Time) and resolve the Simple/Adjusted toggle — both are
  // client-side off the already-fetched rows, so flipping either re-renders with no refetch.
  const resolvedPresetTableData = useMemo<CrossChannelPacingTableData>(
    () => ({
      data: resolvePacingMode(
        presetTableOverTime ? presetOverTimeData : presetSummaryData,
        simplicity === Simplicity.ADJUSTED
      )
    }),
    [presetTableOverTime, presetOverTimeData, presetSummaryData, simplicity]
  );

  // Grouping dimensions offered in the preset modal: the static pacing dims plus the company's custom
  // segments (from filterData).
  const pacingTableDimensions = useMemo<ColumnMetaDataMap>(
    () => ({
      ...pacingDimensionColumnMetaDataMap,
      ...buildCustomSegmentDimensionMap(filterData ?? [])
    }),
    [filterData]
  );

  // Seed a MetricsPagePreset from the current table-tab view to open the editor for a brand-new preset
  // ("Add New"); the editor then saves whatever the user tweaks. Single date range (no comparison).
  // Only the fields that drive the Performance Table are stored — the table's grouping comes from the
  // linked table preset (tableID), not from the bars-tab goal/dimensions/dataMode state. The pacing
  // extras (source/simplicity/tableView) ride globalOptions' open index signature. id 'new' marks it
  // unsaved (bridged into the MetricsPagePreset-typed edit state the shared modal expects).
  const buildTablePagePresetSeed = useCallback(
    (name: string): MetricsPagePreset =>
      ({
        company,
        name,
        mediatype: prefix,
        presetGroup: 'Standard',
        temporary: false,
        id: 'new',
        dateInfo: {
          dateType: 'exact',
          increments: 'daily',
          start: dateRange.start,
          end: dateRange.end
        },
        filterInfo: buildFilterInfo(filterID, filterState),
        globalOptions: {
          isDefault: false,
          role: 10,
          kpi,
          tableID: resolvedTablePreset?.id ?? 0,
          source: selectedSources,
          simplicity,
          tableView
        }
      }) as unknown as MetricsPagePreset,
    [
      company,
      prefix,
      dateRange,
      filterID,
      filterState,
      kpi,
      resolvedTablePreset,
      selectedSources,
      simplicity,
      tableView
    ]
  );

  const pagePreset = useMemo(
    () =>
      pagePresetName && pagePresets ? pagePresets.find(p => p.name === pagePresetName) : undefined,
    [pagePresetName, pagePresets]
  );
  // Content signature of the applied preset. The restore guard keys off this (not just the name) so
  // editing the active preset re-applies once the refetched config lands — its signature changes
  // even though the name doesn't.
  const pagePresetSig = useMemo(
    () =>
      pagePreset
        ? JSON.stringify({
            name: pagePreset.name,
            dateInfo: pagePreset.dateInfo,
            globalOptions: pagePreset.globalOptions,
            filterInfo: pagePreset.filterInfo
          })
        : undefined,
    [pagePreset]
  );

  // Restore page state when a preset is applied. Guarded to run once per applied config; applyPagePreset
  // clears the guard so re-selecting the active preset restores again.
  const [appliedPagePresetSig, setAppliedPagePresetSig] = useState<string>();
  // Apply a page preset by name; clearing the guard lets the restore effect re-run even when
  // re-selecting the preset that's already active.
  const applyPagePreset = useCallback((name: string) => {
    setAppliedPagePresetSig(undefined);
    setPagePresetName(name);
  }, []);
  useEffect(() => {
    // Wait for tablePresets before restoring anything: the effect commits appliedPagePresetSig at the
    // end, so if it ran early (tablePresets not loaded) it would skip the linked tableID and then
    // short-circuit on the sig when tablePresets arrives — never restoring the table preset.
    if (!pagePreset || pagePresetSig === appliedPagePresetSig || !tablePresets) {
      return;
    }
    const { dateInfo, globalOptions, filterInfo } = pagePreset;
    if (dateInfo) {
      // Relative dates re-resolve against today on every apply; exact dates restore as-is.
      // Comparison period / increments aren't used by pacing.
      const { fiscal_cal: fiscalCal } = companyInfo;
      let resolvedDates: DateRange | undefined;
      if (
        dateInfo.dateType === 'relative' &&
        dateInfo.relative_date_info &&
        dateInfo.relative_date_info !== 'Custom'
      ) {
        try {
          resolvedDates =
            typeof dateInfo.relative_date_info === 'string'
              ? computeDateFromPreset(dateInfo.relative_date_info, fiscalCal)
              : computeCustomRelativeRange(
                  dateInfo.relative_date_info.start,
                  dateInfo.relative_date_info.end,
                  fiscalCal
                );
        } catch {
          resolvedDates = undefined;
        }
      } else if (dateInfo.start && dateInfo.end) {
        resolvedDates = { start: dateInfo.start, end: dateInfo.end };
      }
      if (resolvedDates) {
        setDateRange(resolvedDates);
      }
    }
    if (globalOptions) {
      if (globalOptions.kpi) {
        setKpi(globalOptions.kpi);
      }
      if (globalOptions.source) {
        setSelectedSources(
          Array.isArray(globalOptions.source) ? globalOptions.source : [globalOptions.source]
        );
      }
      if (globalOptions.simplicity) {
        setSimplicity(globalOptions.simplicity);
      }
      if (globalOptions.tableView) {
        setTableView(globalOptions.tableView);
      }
      if (globalOptions.tableID != null) {
        const linked = tablePresets.find(p => p.id === globalOptions.tableID);
        if (linked) {
          setTablePresetName(linked.name);
          setTablePresetId(linked.id);
        }
      }
    }
    if (filterInfo) {
      if (filterInfo.filterID != null) {
        setFilterSource('presetFilterID');
        setFilterID(filterInfo.filterID);
      } else if (filterInfo.filterState) {
        setFilterSource('presetState');
        setFilterState(filterInfo.filterState);
      } else {
        // Preset saved with no filter (filterInfo is {}) — clear any filter still applied.
        setFilterSource(undefined);
        setFilterID(undefined);
        resetFilterState();
      }
    }
    setAppliedPagePresetSig(pagePresetSig);
  }, [
    pagePreset,
    pagePresetSig,
    appliedPagePresetSig,
    tablePresets,
    setFilterState,
    resetFilterState,
    companyInfo
  ]);

  // Auto-apply the default page preset once on load when nothing is selected yet. The ref keeps it
  // one-time so clearing the selection later doesn't re-apply it.
  const appliedDefaultPagePreset = useRef(false);
  useEffect(() => {
    // Wait for the initial goal/source/KPI pick before auto-applying: otherwise the source+KPI
    // reconcile effect (which fires when currentGoalValue settles) races the restore and can clobber
    // the preset's restored source/KPI.
    if (appliedDefaultPagePreset.current || pagePresetName || !pagePresets || !initialPickDone) {
      return;
    }
    const defaultPreset = pagePresets.find(
      preset => preset.globalOptions?.isDefault && !preset.temporary
    );
    if (defaultPreset) {
      appliedDefaultPagePreset.current = true;
      applyPagePreset(defaultPreset.name);
    }
  }, [pagePresets, pagePresetName, initialPickDone, applyPagePreset]);

  return (
    <Page
      title={PAGE_NAMES.PACING}
      pageType={PAGE_NAMES.PACING}
      isLoading={pageLoading}
      navs={NAVS}
      selectedNav={tab}
      onNav={nav => {
        goToTab(nav as TabKey);
        Mixpanel.track(MxE.TabChange, { page: PAGE_NAMES.PACING, option_selected: nav });
      }}
      actions={
        <div className='pacing-dashboard-actions'>
          {tab === TabKey.PACING && (
            <>
              <IconButton
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                background={Background.DARK}
                size={Size.MD}
                onClick={() => {
                  if (!showHelpModal) {
                    Mixpanel.track(MxE.ModalOpened, {
                      page: PAGE_NAMES.PACING,
                      modal: 'pacing help'
                    });
                  }
                  setShowHelpModal(v => !v);
                }}
              >
                <MdHelp />
              </IconButton>
              <SegmentedControlButton
                background={Background.DARK}
                options={[DataMode.RELATIVE, DataMode.ABSOLUTE]}
                selectedOption={dataMode}
                onChange={setDataMode}
              />
              {/* Budget paces on delivery (source/KPI-agnostic), so the pickers only apply to the
                  performance goal types. */}
              {goal !== 'Budget' && (
                <SourceKpiPickers
                  selectedSources={selectedSources}
                  setSelectedSources={setSelectedSources}
                  sourceOptions={sourceOptions}
                  uniquePlatforms={uniquePlatforms}
                  selectedKpi={selectedKpi}
                  setKpi={setKpi}
                  kpiOptions={kpiOptions}
                />
              )}
              {/* Ratio goals only render actuals + goal values, so Simple/Adjusted has no effect —
                  hide it (the bars show one goal type at a time). */}
              {!isPacingRatioGoalType(currentGoalValue) && (
                <SegmentedControlButton
                  background={Background.DARK}
                  options={[Simplicity.SIMPLE, Simplicity.ADJUSTED]}
                  selectedOption={simplicity}
                  onChange={setSimplicity}
                />
              )}
            </>
          )}
          {tab === TabKey.PERFORMANCE_TABLE && (
            <>
              <PresetDropdown
                size={Size.MD}
                presetType='page'
                addPresetOnClick={() => {
                  setPagePresetChanges(buildTablePagePresetSeed('My New Page Preset'));
                  setShowNewPagePresetModal(true);
                  Mixpanel.track(MxE.ModalOpened, {
                    page: PAGE_NAMES.PACING,
                    modal: 'new page preset'
                  });
                }}
                addPresetText='Add New Page Preset'
                applyOnClick={preset => applyPagePreset(preset.name)}
                cancelOnClick={() => {}}
                className={cn('pacing-page-preset-picker', { loading: fetchingPagePresets })}
                deleteItemOnClick={presetID =>
                  deletePagePreset({ id: presetID, mediatype: prefix })
                }
                duplicateItemOnClick={(preset: MetricsPagePreset) => {
                  (async () => {
                    const saved = await savePagePreset({
                      company,
                      id: 'new',
                      mediatype: prefix,
                      name: `${preset.name} (Copy)`,
                      group: preset.presetGroup,
                      preset: {
                        ...preset,
                        temporary: false,
                        globalOptions: {
                          ...preset.globalOptions,
                          admin_lock: false,
                          isDefault: false
                        }
                      }
                    });
                    // savePagePreset swallows errors and returns undefined; apply + track only when it saved.
                    if (saved) {
                      applyPagePreset(saved.name);
                      Mixpanel.track(MxE.RecordCreated, {
                        page: PAGE_NAMES.PACING,
                        entity: 'page preset'
                      });
                    }
                  })();
                }}
                editItemOnClick={(preset: MetricsPagePreset) => {
                  setPagePresetChanges(preset);
                  setShowEditPagePresetModal(true);
                  Mixpanel.track(MxE.ModalOpened, {
                    page: PAGE_NAMES.PACING,
                    modal: 'edit page preset'
                  });
                }}
                label={
                  pagePreset && pagePreset.id != null
                    ? `Page Preset: ${pagePreset.temporary ? 'Temp' : pagePreset.name}`
                    : 'Load Page Preset'
                }
                // Hide temporary presets (Temp Preview throwaways) from the list, except the one
                // currently applied.
                presets={(pagePresets ?? []).filter(
                  preset => !preset.temporary || preset.id === pagePreset?.id
                )}
                renameItemOnClick={(preset: MetricsPagePreset) => {
                  setPagePresetChanges(preset);
                  setShowRenamePagePresetModal(true);
                  Mixpanel.track(MxE.ModalOpened, {
                    page: PAGE_NAMES.PACING,
                    modal: 'rename page preset'
                  });
                }}
                selectedPreset={pagePreset}
                setAsDefault={(preset: MetricsPagePreset) =>
                  savePagePreset({
                    company,
                    id: preset.id,
                    mediatype: prefix,
                    name: preset.name,
                    group: preset.presetGroup,
                    preset: {
                      ...preset,
                      globalOptions: { ...preset.globalOptions, isDefault: true }
                    }
                  }).then(saved => {
                    if (saved) {
                      // Confirm with a toast; don't re-apply the preset here.
                      setError({
                        message: `${preset.name} has been set as the default ${prefix} preset.`,
                        title: 'Success!',
                        intent: Intent.SUCCESS
                      });
                      Mixpanel.track(MxE.RecordSaved, {
                        page: PAGE_NAMES.PACING,
                        entity: 'page preset'
                      });
                    }
                  })
                }
              />
              <SourceKpiPickers
                selectedSources={selectedSources}
                setSelectedSources={setSelectedSources}
                sourceOptions={sourceOptions}
                uniquePlatforms={uniquePlatforms}
                selectedKpi={selectedKpi}
                setKpi={setKpi}
                kpiOptions={kpiOptions}
              />
              <SegmentedControlButton
                background={Background.DARK}
                options={[Simplicity.SIMPLE, Simplicity.ADJUSTED]}
                selectedOption={simplicity}
                onChange={setSimplicity}
              />
            </>
          )}
          <DatePicker
            range={dateRange}
            onChange={setDateRange}
            disabled={pageLoading}
            removeHeaderText
            isOutsideRange={() => false}
            datePresets
          />
        </div>
      }
      subHeader={
        tab === TabKey.PERFORMANCE_TABLE && filterData ? (
          <div className='pacing-page-sub-header'>
            <FetchFilterBar
              categories={filterData}
              company={company}
              filterID={filterID}
              filters={filterPresets}
              filterSource={filterSource}
              platform={prefix}
              refreshData={() => queryClient.invalidateQueries({ queryKey: ['pacingTableData'] })}
              setFilterID={setFilterID}
              setFilters={setFilterPresets}
              setFilterSource={
                setFilterSource as Dispatch<
                  SetStateAction<
                    'presetFilterID' | 'currentFilterID' | 'presetState' | 'currentState'
                  >
                >
              }
              setState={setFilterState}
              state={filterState}
            />
          </div>
        ) : undefined
      }
    >
      <div className='pacing-page'>
        {conflictModal && (
          <RatioGoalConflictModal conflicts={conflictModal} onHide={() => setConflictModal(null)} />
        )}
        {(showNewPagePresetModal || showEditPagePresetModal) && pagePresetChanges && (
          <EditPagePresetModal
            mediatype={prefix}
            // Only the tabs a table page preset drives. The built-in Dates tab is reused for exact/
            // relative primary-period selection (singleDatePeriod hides the comparison period +
            // increments pacing doesn't use); Table View is a pacing-specific extraTab.
            menuTabs={['Dates', 'KPI, etc.', 'Filters', 'Metrics Table', TABLE_VIEW_TAB]}
            singleDatePeriod
            // sourceOptions is analytics sources only; prepend Platform (as the table-tab picker
            // does) so it's selectable in the modal.
            globalOptionsPickers={{
              kpi: { options: kpiOptions },
              source: [{ label: 'Platform', value: 'platform' }, ...sourceOptions]
            }}
            filterPresets={filterPresets ?? []}
            // Temporary presets (Temp Preview throwaways) shouldn't be selectable in the Metrics Table
            // tab. They'd also render as bare ids here (no name).
            tablePresets={(tablePresets ?? []).filter(preset => !preset.temporary)}
            pagePresetChanges={pagePresetChanges}
            setPagePresetChanges={
              setPagePresetChanges as Dispatch<SetStateAction<MetricsPagePreset>>
            }
            extraTabs={[
              {
                label: TABLE_VIEW_TAB,
                content: (
                  <PagePresetTableViewTab
                    pagePresetChanges={pagePresetChanges}
                    setPagePresetChanges={setPagePresetChanges}
                  />
                )
              }
            ]}
            onHide={() => {
              setShowEditPagePresetModal(false);
              setShowNewPagePresetModal(false);
            }}
            // id > 0 = update existing; otherwise create ('new'). savePagePreset swallows errors +
            // returns undefined, so apply only on success. "Temp Preview" saves with temporary: true;
            // both paths route through here.
            saveAndApplyOnClick={changes => {
              (async () => {
                const isExisting = typeof changes.id === 'number' && changes.id > 0;
                const saved = await savePagePreset({
                  company,
                  id: isExisting ? changes.id : 'new',
                  mediatype: prefix,
                  name: changes.name,
                  group: changes.presetGroup,
                  preset: changes,
                  temporary: changes.temporary
                });
                if (saved) {
                  applyPagePreset(saved.name);
                  setShowEditPagePresetModal(false);
                  setShowNewPagePresetModal(false);
                  Mixpanel.track(isExisting ? MxE.RecordSaved : MxE.RecordCreated, {
                    page: PAGE_NAMES.PACING,
                    entity: 'page preset'
                  });
                }
              })();
            }}
          />
        )}
        {showRenamePagePresetModal && pagePresetChanges && (
          <RenameModal
            applyOnClick={newName => {
              (async () => {
                const saved = await savePagePreset({
                  company,
                  id: pagePresetChanges.id,
                  mediatype: prefix,
                  name: newName,
                  group: pagePresetChanges.presetGroup,
                  preset: { ...pagePresetChanges, name: newName },
                  temporary: pagePresetChanges.temporary ?? false
                });
                setShowRenamePagePresetModal(false);
                applyPagePreset(newName);
                // savePagePreset swallows errors and returns undefined; track only when it didn't.
                if (saved !== undefined) {
                  Mixpanel.track(MxE.RecordSaved, {
                    page: PAGE_NAMES.PACING,
                    entity: 'page preset'
                  });
                }
              })();
            }}
            applyText='Save'
            cancelText='Discard'
            onCancel={() => setShowRenamePagePresetModal(false)}
            onHide={() => setShowRenamePagePresetModal(false)}
            placeholder='Enter new preset name'
            subTitle='New Page Preset Name'
            title={`Rename ${pagePresetChanges.name ?? ''}`}
          />
        )}
        {/* The page spinner covers only the bars tab's first load; refetches keep the dashboard
            mounted (it overlays the bars itself). The table tab renders and lets
            PacingPresetTableDashboard show its own loading state. */}
        {tab === TabKey.PACING && (!pacingOptions || (!barsData && !barsError)) ? (
          <Spinner />
        ) : (
          <>
            {tab === TabKey.PACING && (
              <PacingDashboard
                pacingBarsData={pacingBarsData}
                start={dateRange.start}
                end={dateRange.end}
                simplicity={simplicity}
                setSimplicity={setSimplicity}
                isActual={dataMode === DataMode.ABSOLUTE}
                showHelpModal={showHelpModal}
                setShowHelpModal={setShowHelpModal}
                activeDimensions={activeDimensions}
                setActiveDimensions={setActiveDimensions}
                currentGoalValue={currentGoalValue}
                setCurrentGoalValue={setCurrentGoalValue}
                company={company}
                goalOptions={pacingOptions?.goalTypes ?? []}
                segmentsPerGoal={pacingOptions?.segmentsPerGoal ?? {}}
                isFetching={barsLoading}
                hasError={Boolean(barsError)}
              />
            )}
            {tab === TabKey.PERFORMANCE_TABLE && (
              <PacingPresetTableDashboard
                company={company}
                pacingTableDataContent={resolvedPresetTableData}
                tableDataReady={presetTableDataReady}
                timePeriodProgress={timePeriodProgress(start, end)}
                tableView={tableView}
                setTableView={setTableView}
                dimensionColumnMetaDataMap={pacingTableDimensions}
                kpi={kpi}
                kpiMetaData={kpiNameMap}
                sources={selectedSources}
                sourceOptions={sourceOptions}
                tablePresets={tablePresets}
                tablePreset={resolvedTablePreset}
                fetchingTablePresets={fetchingTablePresets}
                getTablePreset={getTablePreset}
                saveTablePreset={saveTablePreset}
                deleteTablePreset={deleteTablePreset}
                applyTablePreset={(name, id) => {
                  setTablePresetName(name);
                  setTablePresetId(id);
                }}
              />
            )}
          </>
        )}
      </div>
    </Page>
  );
};
